'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useUploadFile } from '@/features/upload/api';
import { useListMusicTracks } from '@/features/videos/api/list-music-tracks';
import { cn } from '@/lib/utils';
import {
  AlertCircle,
  Loader2,
  Music,
  Pause,
  Play,
  Trash2,
  Upload,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  useCreateMusicUpload,
  useDeleteMusicUpload,
  useListMusicUploads,
} from '../api/music-uploads.hook';
import { useAudioPreview } from '../hooks/use-audio-preview';
import { detectBestMusicStart } from '../lib/best-music-start';

const clock = (totalSec: number) => {
  const sec = Math.round(totalSec);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/** Reads an audio file's length in the browser; undefined if it can't. */
function measureDuration(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (value: number | undefined) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    audio.addEventListener('loadedmetadata', () =>
      done(Number.isFinite(audio.duration) ? audio.duration : undefined)
    );
    audio.addEventListener('error', () => done(undefined));
    setTimeout(() => done(undefined), 5000);
    audio.src = url;
  });
}

/**
 * The music every video can use: the organization's own uploads first, then
 * the shared library — each playable right here. Uploads show up in every
 * "Edit music" picker.
 */
export function MusicLibrary() {
  const { tracks, isLoading, isError, refetch } = useListMusicTracks();
  const { playingId, toggle } = useAudioPreview();
  const { uploads } = useListMusicUploads();
  const { uploadAsync } = useUploadFile({
    purpose: 'org-asset',
    showToast: false,
  });
  const { createMusicUploadAsync } = useCreateMusicUpload();
  const { deleteMusicUpload, deletingId } = useDeleteMusicUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('audio/')) {
      toast.error('Choose an audio file (MP3, M4A, WAV…).');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(
        'That file is over 50 MB — try a shorter or compressed track.'
      );
      return;
    }
    setUploading(true);
    try {
      // The best part is found here, in the browser, so renders start the
      // track on its hook (like the library tracks' built-in skip).
      const [stored, analysis] = await Promise.all([
        uploadAsync(file),
        detectBestMusicStart(file),
      ]);
      // The decode gives the exact length; fall back to the audio element
      // only when the browser couldn't decode the file.
      const durationSec =
        analysis?.durationSec ?? (await measureDuration(file));
      await createMusicUploadAsync({
        name: file.name.replace(/\.[^.]+$/, '') || file.name,
        blobUrl: stored.url,
        ...(durationSec ? { durationSec } : {}),
        ...(analysis ? { startSec: analysis.startSec } : {}),
      });
      toast.success('Music uploaded');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Upload failed — try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Music</h1>
          <p className="text-sm text-muted-foreground">
            Background tracks your videos can use. Play any track to hear it.
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          aria-label="Choose a music file to upload"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void handleFile(file);
          }}
        />
        <Button
          type="button"
          className="gap-2"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Upload className="size-4" />
          )}
          {uploading ? 'Uploading…' : 'Upload music'}
        </Button>
      </div>

      {uploads.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Your uploads
          </h2>
          <ul aria-label="Your uploads" className="divide-y rounded-lg border">
            {uploads.map((upload) => {
              const rowId = `upload-${upload.id}`;
              const playing = playingId === rowId;
              return (
                <li
                  key={upload.id}
                  className={cn(
                    'flex items-center gap-4 px-4 py-3',
                    playing && 'bg-muted/50'
                  )}
                >
                  <Button
                    type="button"
                    size="icon"
                    variant={playing ? 'default' : 'outline'}
                    className="size-10 shrink-0 rounded-full"
                    aria-label={`${playing ? 'Pause' : 'Play'} ${upload.name}`}
                    onClick={() => toggle(rowId, upload.playUrl)}
                  >
                    {playing ? (
                      <Pause className="size-4" />
                    ) : (
                      <Play className="size-4" />
                    )}
                  </Button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{upload.name}</p>
                    <p className="truncate text-sm text-muted-foreground">
                      Uploaded by your team
                    </p>
                  </div>
                  {upload.durationSec ? (
                    <span className="w-12 text-right text-sm text-muted-foreground tabular-nums">
                      {clock(upload.durationSec)}
                    </span>
                  ) : null}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8 shrink-0"
                    aria-label={`Delete ${upload.name}`}
                    disabled={deletingId === upload.id}
                    onClick={() => deleteMusicUpload(upload.id)}
                  >
                    {deletingId === upload.id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Trash2 className="size-4" />
                    )}
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {['a', 'b', 'c', 'd'].map((k) => (
            <Skeleton key={k} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center">
          <AlertCircle className="size-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Couldn't load the music library.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : tracks.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-8 text-center">
          <Music className="size-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No music tracks yet.</p>
        </div>
      ) : (
        <ul aria-label="Music tracks" className="divide-y rounded-lg border">
          {tracks.map((track) => {
            const playing = playingId === track.id;
            return (
              <li
                key={track.id}
                className={cn(
                  'flex items-center gap-4 px-4 py-3',
                  playing && 'bg-muted/50'
                )}
              >
                <Button
                  type="button"
                  size="icon"
                  variant={playing ? 'default' : 'outline'}
                  className="size-10 shrink-0 rounded-full"
                  aria-label={`${playing ? 'Pause' : 'Play'} ${track.name}`}
                  onClick={() => toggle(track.id, track.url)}
                >
                  {playing ? (
                    <Pause className="size-4" />
                  ) : (
                    <Play className="size-4" />
                  )}
                </Button>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{track.name}</p>
                  <p className="truncate text-sm text-muted-foreground">
                    {track.artist ?? 'Borrah library'}
                  </p>
                </div>
                {track.mood && (
                  <Badge
                    variant="secondary"
                    className="hidden capitalize sm:inline-flex"
                  >
                    {track.mood}
                  </Badge>
                )}
                <span className="hidden w-16 text-right text-sm text-muted-foreground tabular-nums sm:block">
                  {track.bpm} BPM
                </span>
                <span className="w-12 text-right text-sm text-muted-foreground tabular-nums">
                  {clock(track.durationSec)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
